import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { searchNews, searchVideos, news, videos, formatDate, formatViews } from '@/utils/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsListItem from '@/components/feature/NewsListItem';
import VideoCard from '@/components/feature/VideoCard';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<'news' | 'video'>('news');

  useEffect(() => {
    setInput(q);
  }, [q]);

  const newsResults = useMemo(() => searchNews(q), [q]);
  const videoResults = useMemo(() => searchVideos(q), [q]);

  usePageMeta(
    q ? `“${q}”的搜索结果 - 91.con` : '全站搜索 - 91.con',
    q ? `在 91.con 搜索“${q}”，为你找到相关的娱乐资讯与视频内容。` : '使用 91.con 全站搜索，快速查找娱乐资讯与视频资源。',
    '搜索,91.con,娱乐资讯,视频资源,热点新闻'
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setParams(value ? { q: value } : {});
  };

  const hotNews = [...news].sort((a, b) => b.views - a.views).slice(0, 3);
  const hotVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 3);
  const hasKeyword = q.trim().length > 0;

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '全站搜索' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-4">全站搜索</h1>
          <p className="text-sm text-foreground-600 mt-3">支持资讯与视频双向搜索，基于标题与关键词匹配（前端模拟演示）。</p>

          <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch gap-2 mt-6 max-w-3xl">
            <div className="flex items-center gap-2 flex-1 bg-background-50 border border-background-200 rounded-md px-3.5 h-12 focus-within:border-primary-500 transition-colors">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如：悬疑、综艺、音乐节"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        {hasKeyword ? (
          <>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
              <button
                type="button"
                onClick={() => setTab('news')}
                className={`px-5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === 'news' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                资讯 {newsResults.length}
              </button>
              <button
                type="button"
                onClick={() => setTab('video')}
                className={`px-5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === 'video' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                视频 {videoResults.length}
              </button>
            </div>

            <p className="text-sm text-foreground-500 mt-6">
              关键词 <span className="text-primary-400 font-semibold">“{q}”</span> 共找到{' '}
              <span className="text-accent-400 font-semibold">{newsResults.length + videoResults.length}</span> 条结果
            </p>

            {tab === 'news' && (
              <div className="flex flex-col gap-4 mt-6">
                {newsResults.length > 0 ? (
                  newsResults.map((item) => <NewsListItem key={item.id} item={item} />)
                ) : (
                  <EmptyState keyword={q} />
                )}
              </div>
            )}

            {tab === 'video' && (
              <>
                {videoResults.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-6">
                    {videoResults.map((item) => (
                      <VideoCard key={item.id} item={item} />
                    ))}
                  </div>
                ) : (
                  <EmptyState keyword={q} />
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center py-10">
            <i className="ri-search-eye-line text-5xl text-foreground-500" />
            <p className="text-sm text-foreground-600 mt-4">输入关键词开始搜索，或看看下面的热门内容</p>
          </div>
        )}

        {(newsResults.length === 0 || videoResults.length === 0 || !hasKeyword) && (
          <div className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              为你推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {hotVideos.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
            <div className="flex flex-col gap-4 mt-6">
              {hotNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-center gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
                >
                  <div className="w-24 h-16 rounded-md overflow-hidden shrink-0">
                    <img src={item.cover} alt={item.title} title={item.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1.5">
                      <span>{item.categoryName}</span>
                      <span>{formatDate(item.date)}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line" />{formatViews(item.views)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}

function EmptyState({ keyword }: { keyword: string }) {
  return (
    <div className="text-center py-16">
      <i className="ri-file-search-line text-4xl text-foreground-500" />
      <p className="text-sm text-foreground-600 mt-3">没有找到与“{keyword}”相关的内容，试试其他关键词吧</p>
    </div>
  );
}